import React, { useState, useCallback } from 'react';
import { Heart, MessageCircle, ChevronLeft, ChevronRight, Trash2, MoreVertical, Send, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { LevelBadge } from './LevelBadge';
import axios from 'axios';
import { API, resolvePhotoUrl, avatarUrl } from '../utils/api';
import { toast } from 'sonner';

const formatTimeRemaining = (hoursRemaining) => {
  if (!hoursRemaining && hoursRemaining !== 0) return null;
  if (hoursRemaining < 1) {
    const minutes = Math.round(hoursRemaining * 60);
    return `${minutes}min`;
  } else if (hoursRemaining < 24) {
    const hours = Math.floor(hoursRemaining);
    const minutes = Math.round((hoursRemaining - hours) * 60);
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }
  return '24h';
};

export const PostCard = React.memo(({ post, onPatchPost, onRemovePost, currentUser }) => {
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const date = new Date(post.created_at);
  const userId = currentUser?.id;
  const userRole = currentUser?.role;
  const isOwner = userId === post.user_id;
  const isAdmin = userRole === 'admin';
  const canDelete = isOwner || isAdmin;

  const postPhotos = post.photo_urls || [];
  const legacyPhoto = post.photo_url;
  const photos = postPhotos.length > 0 ? postPhotos : (legacyPhoto ? [legacyPhoto] : []);

  const postLikedBy = post.liked_by || [];
  const isLiked = userId && postLikedBy.includes(userId);
  const postComments = post.comments || [];

  const handleLike = useCallback(async () => {
    if (isLiking || !userId) return;
    setIsLiking(true);

    // Optimistic update
    const wasLiked = postLikedBy.includes(userId);
    const newLikedBy = wasLiked
      ? postLikedBy.filter(id => id !== userId)
      : [...postLikedBy, userId];
    const newLikes = wasLiked ? (post.likes - 1) : (post.likes + 1);

    onPatchPost(post.id, { likes: newLikes, liked_by: newLikedBy });

    try {
      await axios.post(`${API}/feed/${post.id}/like`);
    } catch (error) {
      // Revert on error
      onPatchPost(post.id, { likes: post.likes, liked_by: postLikedBy });
      console.error('Error liking post:', error);
    } finally {
      setIsLiking(false);
    }
  }, [isLiking, userId, post.id, post.likes, postLikedBy, onPatchPost]);

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;
    if (!window.confirm('Tem certeza que deseja excluir este post?')) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/feed/${post.id}`);
      toast.success('Post excluído com sucesso');
      onRemovePost(post.id);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao excluir post';
      toast.error(msg);
      setIsDeleting(false);
      setShowMenu(false);
    }
  }, [isDeleting, post.id, onRemovePost]);

  const handleAddComment = useCallback(async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment || !currentUser) return;
    setSubmittingComment(true);

    try {
      const res = await axios.post(`${API}/feed/${post.id}/comment`, { content: newComment.trim() });
      // Patch with real comment from server
      onPatchPost(post.id, { comments: [...postComments, res.data] });
      setNewComment('');
    } catch (error) {
      toast.error('Erro ao adicionar comentário');
    } finally {
      setSubmittingComment(false);
    }
  }, [newComment, submittingComment, currentUser, post.id, postComments, onPatchPost]);

  const handleDeleteComment = useCallback(async (commentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este comentário?')) return;

    // Optimistic remove
    const filtered = postComments.filter(c => c.id !== commentId);
    onPatchPost(post.id, { comments: filtered });

    try {
      await axios.delete(`${API}/feed/${post.id}/comments/${commentId}`);
      toast.success('Comentário excluído');
    } catch (error) {
      // Revert
      onPatchPost(post.id, { comments: postComments });
      toast.error(error.response?.data?.detail || 'Erro ao excluir comentário');
    }
  }, [post.id, postComments, onPatchPost]);

  const nextPhoto = () => setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  const prevPhoto = () => setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);

  const canDeleteComment = (comment) => userId && (userId === comment.user_id || isAdmin);

  const userName = post.user_name || '';
  const userInitial = userName.charAt(0);
  const timeRemaining = formatTimeRemaining(post.time_remaining_hours);
  const isExpiringSoon = post.time_remaining_hours && post.time_remaining_hours < 2;

  return (
    <div
      className="bg-zinc-900/50 border border-white/5 backdrop-blur-md rounded-2xl overflow-hidden"
      data-testid="post-card"
    >
      <div className="p-4 pb-3">
        <div className="flex items-start gap-3">
          {post.user_avatar_v ? (
            <img src={avatarUrl(post.user_id, post.user_avatar_v)} alt={userName} loading="lazy" decoding="async" className="w-12 h-12 rounded-full border-2 border-neon-lime/30 object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-bold text-lg">
              {userInitial}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-secondary font-bold text-white">{userName}</h3>
              <div className="scale-75 origin-left">
                <LevelBadge level={post.user_level} size="sm" />
              </div>
            </div>
            <p className="text-xs text-zinc-400 flex items-center gap-2">
              <span>{date.toLocaleString('pt-BR')}</span>
              {timeRemaining && (
                <span className={`flex items-center gap-1 ${isExpiringSoon ? 'text-orange-400' : 'text-zinc-500'}`}>
                  <Clock className="w-3 h-3" />
                  {timeRemaining}
                </span>
              )}
            </p>
          </div>

          {canDelete && (
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors">
                <MoreVertical className="w-5 h-5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 overflow-hidden min-w-[140px]">
                  <button onClick={handleDelete} disabled={isDeleting} className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-red-500/20 transition-colors text-sm">
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Excluindo...' : 'Excluir post'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {photos.length > 0 && (
        <div className="relative">
          <img src={resolvePhotoUrl(photos[currentPhotoIndex])} alt="Post" className="w-full aspect-square object-cover" />
          {photos.length > 1 && (
            <>
              <button onClick={prevPhoto} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={nextPhoto} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {photos.map((photo, index) => (
                  <button key={photo + index} onClick={() => setCurrentPhotoIndex(index)} className={index === currentPhotoIndex ? 'w-4 h-2 rounded-full bg-neon-lime transition-all' : 'w-2 h-2 rounded-full bg-white/50 hover:bg-white/80 transition-all'} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="p-4">
        {post.content && <p className="text-zinc-200 mb-3 whitespace-pre-wrap">{post.content}</p>}

        <div className="flex items-center gap-4 pt-3 border-t border-zinc-800">
          <motion.button
            onClick={handleLike}
            className={isLiked ? 'flex items-center gap-2 text-red-500 transition-colors' : 'flex items-center gap-2 text-zinc-400 hover:text-red-500 transition-colors'}
            whileTap={{ scale: 0.9 }}
            data-testid="like-button"
          >
            <Heart className={isLiked ? 'w-5 h-5 fill-current' : 'w-5 h-5'} />
            <span>{post.likes}</span>
          </motion.button>
          <button
            onClick={() => setShowComments(!showComments)}
            className={showComments ? 'flex items-center gap-2 text-neon-lime transition-colors' : 'flex items-center gap-2 text-zinc-400 hover:text-neon-lime transition-colors'}
            data-testid="comment-button"
          >
            <MessageCircle className="w-5 h-5" />
            <span>{postComments.length}</span>
          </button>
        </div>

        {showComments && (
          <div className="mt-4 pt-4 border-t border-zinc-800">
            {postComments.length > 0 && (
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {postComments.map((comment) => {
                  const commentUserName = comment.user_name || '';
                  const commentInitial = commentUserName.charAt(0);
                  return (
                    <div key={comment.id} className="flex items-start gap-2 group">
                      <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {commentInitial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{commentUserName}</span>
                          <span className="text-xs text-zinc-500">{new Date(comment.created_at).toLocaleString('pt-BR')}</span>
                        </div>
                        <p className="text-sm text-zinc-300 break-words">{comment.content}</p>
                      </div>
                      {canDeleteComment(comment) && (
                        <button onClick={() => handleDeleteComment(comment.id)} className="p-1 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all" title="Excluir comentário">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <form onSubmit={handleAddComment} className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escreva um comentário..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder-zinc-500 focus:border-neon-lime focus:outline-none"
                data-testid="comment-input"
              />
              <button
                type="submit"
                disabled={!newComment.trim() || submittingComment}
                className="p-2 bg-neon-lime text-black rounded-full hover:bg-neon-lime/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="submit-comment-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
});

PostCard.displayName = 'PostCard';
